import { getContext, setContext } from 'svelte';
import { t } from '#lib/i18n/tranlsator.svelte.ts';
import { globalState } from '#lib/util/global-state.svelte.ts';
import {
	attachFile,
	deleteAttachment,
	type PendingAttachment
} from '#lib/util/chat-attachments.svelte.ts';
import type { AssetRef } from '#src/types/chat.ts';

/** The files attached to the message being written on a page, wherever they come from:
 *  the attach button, a drop or a paste. */
export class AttachmentsContext {
	items: PendingAttachment[] = $state([]);

	/** Over the per-message limits, which every upload's response carries: the
	 *  backend is the only place they live. */
	limitMessage: string | null = $derived.by(() => {
		const limits = this.items.find((item) => item.asset?.limits)?.asset?.limits;
		if (!limits) {
			return null;
		}
		if (this.items.length > limits.maxFilesPerMessage) {
			return t('chat.attachment.error.tooManyFiles', { limit: limits.maxFilesPerMessage });
		}
		const tokens = this.items.reduce((sum, item) => sum + (item.asset?.tokenCount ?? 0), 0);
		if (tokens > limits.maxTokensPerMessage) {
			return t('chat.attachment.error.tooLongTogether');
		}
		return null;
	});

	/** Send waits for every upload, and for failed ones to be removed. */
	blocksSending = $derived(
		this.items.some((item) => item.upload.fetching || item.upload.error) ||
			this.limitMessage !== null
	);

	/** What the message carries: the uploaded files as asset references. */
	assetRefs: AssetRef[] = $derived(
		this.items.flatMap((item) => {
			if (!item.asset) {
				return [];
			}
			const { assetId, kind, source, status, name, url, pageCount } = item.asset;
			return [{ assetId, kind, source, status, name, url, pageCount }];
		})
	);

	/** Standard instances with the attachments flag on (antonio instances upload
	 *  through their own flow). Mirrors instanceAllowsUploads in
	 *  backend/routes/asset_upload.go. */
	get enabled() {
		const instance = globalState.instance;
		return instance?.type !== 'antonio' && instance?.feature_flags?.attachments?.enabled === true;
	}

	/** Attaches the files and starts uploading them. */
	add(files: FileList) {
		this.items = [...this.items, ...[...files].map((file) => attachFile(file))];
	}

	/** Takes the file off the message and deletes its upload. */
	remove(item: PendingAttachment) {
		this.items = this.items.filter((other) => other !== item);
		deleteAttachment(item);
	}

	/** Empties the list once the message is sent: the files now belong to it. */
	clear() {
		this.items = [];
	}
}

const ATTACHMENTS_CONTEXT_KEY = Symbol('attachments');

/** Undefined where nothing set it, e.g. in the embeddable widget, which has no logged-in user to own an upload. */
export function getAttachmentsContext() {
	return getContext<AttachmentsContext | undefined>(ATTACHMENTS_CONTEXT_KEY);
}

export function setAttachmentsContext(ctx: AttachmentsContext) {
	setContext(ATTACHMENTS_CONTEXT_KEY, ctx);
	return ctx;
}
